import { Component, ElementRef, inject, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { FileUploadModule } from 'primeng/fileupload';
import { FloatLabelModule } from 'primeng/floatlabel';
import { FluidModule } from 'primeng/fluid';
import { InputTextModule } from 'primeng/inputtext';
import { NotificationService } from '../../../shared/components/notification-modal/notification.service';
import { UserService } from '../../../core/services/api/user.service';
import { UserDataDTO } from '../../../core/models/auth.models';
import { NfcService } from '../../../core/services/api/nfc.service';

@Component({
    selector: 'app-account',
    imports: [FluidModule, InputTextModule, FloatLabelModule, ButtonModule, Dialog, ReactiveFormsModule, FormsModule, FileUploadModule],
    templateUrl: './account.component.html',
    styleUrl: './account.component.scss'
})
export class AccountComponent {
    private fb = inject(FormBuilder);
    private userService = inject(UserService);
    private nfcService = inject(NfcService);

    @ViewChild('signatureCanvas') signatureCanvas!: ElementRef<HTMLCanvasElement>;
    signatureCtx: CanvasRenderingContext2D | null = null;
    isDrawing = false;
    lastX = 0;
    lastY = 0;
    securePin = '';
    existingSignature = '';
    signaturePreview = '';
    signatureDialogVisible = false;
    private originalPin = '';

    editUserData: boolean = false;
    editBillingData: boolean = false;
    uploadedFiles: any[] = [];

    currentUserData?: UserDataDTO;

    currentUserId?: number;

    formUserUpd!: FormGroup;
    formUserBillingUpd!: FormGroup;

    quantityPurchases: number = 0;
    quantityFavorites: number = 0;
    quantityCart: number = 0;

    constructor(private notifier: NotificationService) {
        this.formUserUpd = this.fb.group({
            userNameUpd: [''],
            lastNameUpd: [''],
            addressUserUpd: [''],
            ciUserUpd: [''],
            telUserUpd: [''],
            emailUpd: [''],
            userUpd: [''],
            passwordUpd: [''],
            passwordReUpd: ['']
        });
        this.formUserUpd.disable();
    }

    ngAfterViewInit() {
        this.initSignatureCanvas();
    }

    initSignatureCanvas() {
        const canvas = this.signatureCanvas?.nativeElement;
        if (!canvas) return;
        this.signatureCtx = canvas.getContext('2d');
        if (this.signatureCtx) {
            this.signatureCtx.strokeStyle = '#000';
            this.signatureCtx.lineWidth = 2.5;
            this.signatureCtx.lineCap = 'round';
            this.signatureCtx.lineJoin = 'round';
        }
    }

    scaleX(e: MouseEvent | Touch): number {
        const rect = this.signatureCanvas.nativeElement.getBoundingClientRect();
        return (e.clientX - rect.left) * (this.signatureCanvas.nativeElement.width / rect.width);
    }

    scaleY(e: MouseEvent | Touch): number {
        const rect = this.signatureCanvas.nativeElement.getBoundingClientRect();
        return (e.clientY - rect.top) * (this.signatureCanvas.nativeElement.height / rect.height);
    }

    onMouseDown(event: MouseEvent) {
        this.isDrawing = true;
        const pos = { x: this.scaleX(event), y: this.scaleY(event) };
        this.lastX = pos.x;
        this.lastY = pos.y;
    }

    onMouseMove(event: MouseEvent) {
        if (!this.isDrawing) return;
        const pos = { x: this.scaleX(event), y: this.scaleY(event) };
        this.drawLine(pos.x, pos.y);
    }

    onMouseUp() { this.isDrawing = false; }

    onTouchStart(event: TouchEvent) {
        event.preventDefault();
        const touch = event.touches[0];
        this.isDrawing = true;
        const pos = { x: this.scaleX(touch), y: this.scaleY(touch) };
        this.lastX = pos.x;
        this.lastY = pos.y;
    }

    onTouchMove(event: TouchEvent) {
        event.preventDefault();
        if (!this.isDrawing) return;
        const touch = event.touches[0];
        const pos = { x: this.scaleX(touch), y: this.scaleY(touch) };
        this.drawLine(pos.x, pos.y);
    }

    drawLine(x: number, y: number) {
        const ctx = this.signatureCtx;
        if (!ctx) return;
        ctx.beginPath();
        ctx.moveTo(this.lastX, this.lastY);
        ctx.lineTo(x, y);
        ctx.stroke();
        this.lastX = x;
        this.lastY = y;
    }

    clearSignature() {
        const ctx = this.signatureCtx;
        const canvas = this.signatureCanvas?.nativeElement;
        if (ctx && canvas) {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
    }

    openSignatureDialog() {
        this.signatureDialogVisible = true;
        setTimeout(() => {
            this.initSignatureCanvas();
            this.clearSignature();
        }, 100);
    }

    acceptSignature() {
        const canvas = this.signatureCanvas?.nativeElement;
        const ctx = this.signatureCtx;
        if (!canvas || !ctx) return;
        const hasDrawing = !ctx.getImageData(0, 0, canvas.width, canvas.height).data.every(p => p === 0);
        if (hasDrawing) {
            this.signaturePreview = canvas.toDataURL('image/png');
        }
        this.signatureDialogVisible = false;
    }

    saveProfile() {
        const data: any = {};
        if (this.signaturePreview) data.signature = this.signaturePreview;
        if (this.securePin) data.securePin = this.securePin;
        if (!data.signature && !data.securePin) {
            this.error('Debe cargar una firma o ingresar un PIN', null, 1500);
            return;
        }
        this.userService.updateProfile(data).subscribe({
            next: () => {
                this.success('Perfil actualizado exitosamente', null, 1500);
                if (data.signature) {
                    this.existingSignature = data.signature;
                    this.signaturePreview = '';
                }
                if (data.securePin) {
                    this.originalPin = data.securePin;
                    this.securePin = data.securePin;
                }
            },
            error: (err) => {
                this.error(err.error?.message || 'Error al actualizar perfil', null, 1500);
            }
        });
    }

    ngOnInit(): void {
        this.loadCurrentUser();
        this.userService.getCurrentUser().subscribe({
            next: (response) => {
                console.log(response);
                this.formUserUpd.patchValue({
                    userNameUpd: response.data.person.firstName,
                    lastNameUpd: response.data.person.lastName,
                    addressUserUpd: response.data.person.address,
                    ciUserUpd: response.data.person.documentNumber,
                    telUserUpd: response.data.person.phone,
                    emailUpd: response.data.person.email,
                    userUpd: response.data.username,
                    passwordUpd: response.data.person.firstName,
                    passwordReUpd: response.data.person.firstName
                });
                this.existingSignature = response.data.signature || '';
                this.securePin = response.data.securePin || '';
            },
            error: (error) => {
                console.error('Error al cargar datos del usuario:', error);
            }
        });
    }

    loadCurrentUser() {
        // Obtener ID del usuario del localStorage con la clave 'usr'
        const userStr = localStorage.getItem('user_info');
        console.log('🔍 localStorage usr:', userStr);

        if (userStr) {
            const user = JSON.parse(userStr);
            console.log('👤 Usuario parseado:', user);

            // El objeto puede tener idUser, id_user o id
            this.currentUserId = user.idUser || user.id_user || user.id;
            console.log('🆔 ID del usuario:', this.currentUserId);

            if (this.currentUserId) {
                this.loadUserData();
                this.loadResume();
            } else {
                console.warn('⚠️ No se encontró el ID del usuario en localStorage');
                console.log('📋 Claves disponibles en el objeto usuario:', Object.keys(user));
            }
        } else {
            console.warn('⚠️ No se encontró el usuario en localStorage');
        }
    }

    loadUserData() {
        if (!this.currentUserId) return;

        // this.userService.getUserData(this.currentUserId).subscribe({
        //     next: (response) => {
        //         if (response.success && response.data) {
        //             this.currentUserData = response.data;
        //             this.formUserUpd.patchValue({
        //                 userNameUpd: this.currentUserData?.firstName || '',
        //                 lastNameUpd: this.currentUserData?.lastName || '',
        //                 addressUserUpd: this.currentUserData?.address || '',
        //                 ciUserUpd: this.currentUserData?.documentNumber || '',
        //                 telUserUpd: this.currentUserData?.phone || '',
        //                 userUpd: this.currentUserData?.username || '',
        //                 emailUpd: this.currentUserData?.email || '',
        //                 passwordUpd: this.currentUserData?.password || '',
        //                 passwordReUpd: this.currentUserData?.password || ''
        //             });
        //         }
        //     },
        //     error: (error) => {
        //         console.error('Error al cargar datos del usuario:', error);
        //     }
        // });
    }

    loadResume() {
        if (!this.currentUserId) return;

        // this.userService.getResumeUser(this.currentUserId).subscribe({
        //     next: (response) => {
        //         if (response.success && response.data) {
        //             console.log(response.data);
        //             this.quantityPurchases = response.data.quantityPurchases;
        //             this.quantityFavorites = response.data.quantityFavorites;
        //             this.quantityCart = response.data.quantityCart;
        //         }
        //     },
        //     error: (error) => {
        //         console.error('Error al cargar datos del usuario:', error);
        //     }
        // });
    }

    onUpload(event: any) {
        for (const file of event.files) {
            this.uploadedFiles.push(file);
        }
        // TODO: Implementar subida de imagen cuando esté lista la funcionalidad
    }

    editUser() {
        this.originalPin = this.securePin;
        this.formUserUpd.enable();
        this.formUserUpd.get('userNameUpd')?.disable();
        this.formUserUpd.get('lastNameUpd')?.disable();
        this.formUserUpd.get('addressUserUpd')?.disable();
        this.formUserUpd.get('ciUserUpd')?.disable();
        this.formUserUpd.get('telUserUpd')?.disable();
        this.formUserUpd.get('emailUpd')?.disable();
        this.formUserUpd.get('userUpd')?.disable();
        this.editUserData = true;
    }

    cancelEditUser() {
        this.formUserUpd.disable();
        this.editUserData = false;
        this.signaturePreview = '';
        this.securePin = this.originalPin;
        this.loadUserData();
    }

    saveUser() {
        if (this.formUserUpd.get('passwordUpd')?.value !== this.formUserUpd.get('passwordReUpd')?.value) {
            this.error('Las contraseñas no coinciden', null, 1500);
        } else {
            // if (this.formUserUpd.valid && this.currentUserId) {
            // const formValue = this.formUserUpd.getRawValue();
            // const userData: UserDataDTO = {
            //     firstName: formValue.userNameUpd,
            //     lastName: formValue.lastNameUpd,
            //     phone: formValue.telUserUpd,
            //     documentNumber: formValue.ciUserUpd,
            //     username: formValue.userUpd,
            //     email: formValue.emailUpd,
            //     idUser: this.currentUserId,
            //     address: formValue.addressUserUpd,
            //     password: formValue.passwordUpd
            // };
            // this.userService.updateUser(userData).subscribe({
            //     next: (response) => {
            //         if (response.status) {
            //             // console.log('Usuario actualizado exitosamente:', response);
            //             this.success('Usuario actualizado exitosamente:', null, 1500);
            //             this.formUserUpd.disable();
            //             this.editUserData = false;
            //             this.loadUserData();
            //         } else {
            //             this.error('Error al actualizar los datos del usuario', null, 1500);
            //         }
            //     },
            //     error: (error) => {
            //         console.error('Error al actualizar usuario:', error);
            //         this.error('Error al actualizar los datos del usuario', null, 1500);
            //         // alert('Error al actualizar los datos del usuario');
            //     }
            // });
            // }
        }
    }

    savePassword() {
        if (this.formUserUpd.get('passwordUpd')?.value !== this.formUserUpd.get('passwordReUpd')?.value) {
            this.error('Las contraseñas no coinciden', null, 1500);
        } else {
            let passwordUpd = this.formUserUpd.get('passwordUpd')?.value;

            this.nfcService.verifyCurrentCard().subscribe({
                next: (isValid) => {
                    if (isValid) {
                        this.userService.updatePassword(passwordUpd).subscribe({
                            next: (response) => {
                                console.log(response);

                                if (response.success) {
                                    // console.log('Usuario actualizado exitosamente:', response);
                                    this.success('CONTRASEÑA actualizada con exito', null, 1500);
                                    this.formUserUpd.disable();

                                    let username = response.data.username;
                                    let password = passwordUpd;

                                    let whriteNFCdata = {
                                        username: username,
                                        password: password
                                    };

                                    this.nfcService.verifyCurrentCard().subscribe({
                                        next: (isValid) => {
                                            if (isValid) {
                                                this.nfcService.saveNfcDataToken(whriteNFCdata).subscribe({
                                                    next: (response) => {
                                                        console.log('Datos NFC guardados en el servidor:', response);
                                                    },
                                                    error: (error) => {
                                                        console.error('Error al guardar datos NFC:', error);
                                                    }
                                                });
                                            } else {
                                                console.log('ERROR al actualizar la tarjeta');

                                                // this.nfcService.clearNfcData();
                                            }
                                        },
                                        error: (error) => {
                                            console.log('ERROR al actualizar la tarjeta');

                                            console.error('Error al verificar tarjeta NFC:', error);
                                            // this.nfcService.clearNfcData();
                                        }
                                    });

                                    this.editUserData = false;
                                    this.loadUserData();
                                } else {
                                    this.error('Error al actualizar los datos del usuario', null, 1500);
                                }
                            },
                            error: (error) => {
                                console.error('Error al actualizar usuario:', error);
                                this.error('Error al actualizar los datos del usuario', null, 1500);
                                // alert('Error al actualizar los datos del usuario');
                            }
                        });
                    } else {
                        this.error('Inserte Tarjeta NFC para ACTUALIZAR LA CONTRASEÑA', null, 1500);
                        // this.nfcService.clearNfcData();
                    }
                },
                error: (error) => {
                    console.error('Error al verificar tarjeta NFC:', error);
                    // this.nfcService.clearNfcData();
                }
            });

            // if (this.formUserUpd.valid && this.currentUserId) {
            // const formValue = this.formUserUpd.getRawValue();

            // const userData: UserDataDTO = {
            //     firstName: formValue.userNameUpd,
            //     lastName: formValue.lastNameUpd,
            //     phone: formValue.telUserUpd,
            //     documentNumber: formValue.ciUserUpd,
            //     username: formValue.userUpd,
            //     email: formValue.emailUpd,
            //     idUser: this.currentUserId,
            //     address: formValue.addressUserUpd,
            //     password: formValue.passwordUpd
            // };

            // this.userService.updateUser(userData).subscribe({
            //     next: (response) => {
            //         if (response.status) {
            //             // console.log('Usuario actualizado exitosamente:', response);
            //             this.success('Usuario actualizado exitosamente:', null, 1500);
            //             this.formUserUpd.disable();
            //             this.editUserData = false;
            //             this.loadUserData();
            //         } else {
            //             this.error('Error al actualizar los datos del usuario', null, 1500);
            //         }
            //     },
            //     error: (error) => {
            //         console.error('Error al actualizar usuario:', error);
            //         this.error('Error al actualizar los datos del usuario', null, 1500);
            //         // alert('Error al actualizar los datos del usuario');
            //     }
            // });

            // }
        }
    }

    success(mensaje: string, imagen: string | null, time: number) {
        this.notifier.success(mensaje, imagen!, time);
    }

    error(mensaje: string, imagen: string | null, time: number) {
        this.notifier.error(mensaje, imagen!, time);
    }
}
